import { SparkResult } from "ags/integration/model/results/spark-result";
import Components from "slotworks/components/components";
import SymbolComponent from "slotworks/components/matrix/symbol/symbol-component";
import Layers from "slotworks/graphics/layers/layers";
import AbstractMediator from "slotworks/mediators/abstract-mediator";
import GameState from "slotworks/model/game-state";
import BonusResult from "slotworks/model/gameplay/records/results/bonus-result";
import LineResult from "slotworks/model/gameplay/records/results/line-result";
import Result from "slotworks/model/gameplay/records/results/result";
import ScatterResult from "slotworks/model/gameplay/records/results/scatter-result";
import SymbolWinResult from "slotworks/model/gameplay/records/results/symbol-win-result";
import WaysResult from "slotworks/model/gameplay/records/results/ways-result";
import SpinRecord from "slotworks/model/gameplay/records/spin-record";
import SlotDefinition from "slotworks/model/slot-definition";
import Contract from "slotworks/utils/contracts/contract";
import Parallel from "slotworks/utils/contracts/parallel";
import FreespinScatterResult from "ags/integration/builders/results/freespin-scatter-result";

export default class LoopWinsMediator extends AbstractMediator {

    protected wins: any[] = [];
    protected currentWin: number;

    protected startContract: Contract<void>;

    public start(): Contract<void> {

        this.wins = this.getWinsToAnimate();

        this.wins.forEach((win) => {
            if (!win.positions) { return; }
            for (const position of win.positions) {
                Components.matrix.getSymbol(position.x, position.y).setLayer(Layers.SymbolAnimations);
            }
        });

        this.startContract = this.nextWin();

        return this.startContract;
    }

    public end(): Contract<void> {
        return new Contract<void>((resolve) => {
            this.wins.forEach((win) => {
                if (!win.positions) { return; }
                for (const position of win.positions) {
                    Components.matrix.getSymbol(position.x, position.y).setLayer(Layers.MatrixContent);
                }
            });
            this.startContract.cancel();
            this.wins = null;
            resolve(null);
        });
    }

    protected getWinsToAnimate(): any[] {
        const record = GameState.getCurrentGame().getCurrentRecord() as SpinRecord;
        const wins: Result[] = record.getResultsOfType(SymbolWinResult) as WaysResult[];
        this.currentWin = 0;

        const bonusResults = record.getResultsOfType(BonusResult);
        const sparkResults = record.getResultsOfType(SparkResult);

        if (bonusResults.length > 0 && sparkResults.length > 0) {
            const bonusSparkResult = new BonusResult(bonusResults[0].id, true);
            bonusSparkResult.cashWon = bonusResults[0].cashWon;
            wins.push(sparkResults[0]);
        } else if (bonusResults.length > 0) {
            wins.push(bonusResults[0]);
        }

        return wins.sort((a, b) => a.getPriority() - b.getPriority());
    }

    protected getCelebrationsForWin(win: any) {

        if (win instanceof BonusResult || win instanceof ScatterResult) {
            return [
                () => Components.celebrateWin.miscWin(win, "progressive_win_footer"),
                () => Contract.getTimeoutContract(1000)
            ];
        }

        if (win instanceof SparkResult) {
            return [
                () => this.showWin(win),
                () => Components.celebrateWin.miscWin(win, "progressive_win_footer")
            ];
        }

        const symbolDef = SlotDefinition.getSymbolDefinition(win.symbolType);
        const symbolName = symbolDef ? symbolDef.name : "";

        if (win instanceof LineResult) {
            return [
                () => Components.celebrateWin.lineWin(win, symbolName),
                () => this.showWin(win)
            ];
        }

        if (win instanceof FreespinScatterResult) {
            return [
                () => Components.celebrateWin.scatterWin(win),
                () => this.showWin(win)
            ];
        }

        return [
            () => Components.celebrateWin.waysWin(win, symbolName),
            () => this.showWin(win)
        ];
    }

    protected showCelebrationForSymbols(symbols: SymbolComponent[]): Contract<void> {
        return new Parallel<void>(symbols.map((symbol) => () => symbol.cycle()));
    }

    protected nextWin() {
        return new Contract<void>((resolve) => {
            const win = this.wins[this.currentWin];
            const celebrations = this.getCelebrationsForWin(win);

            new Parallel(celebrations).then(() => {
                if (!this.wins || !this.wins.length) {
                    resolve(null);
                    return;
                }

                this.currentWin++;
                if (this.currentWin < this.wins.length) {
                    this.nextWin().then(resolve);
                } else {
                    resolve(null);
                }
            });
        });
    }

    protected showWin(win: WaysResult | SparkResult | ScatterResult | LineResult): Contract<void> {
        const winningSymbols = [];

        for (const position of win.positions) {
            winningSymbols.push(Components.matrix.getSymbol(position.x, position.y));
        }

        if (!winningSymbols.length) {
            return Contract.getTimeoutContract(1000);
        }

        return this.showCelebrationForSymbols(winningSymbols);
    }
}
